import { List } from '@mui/material';
import Box from '@mui/material/Box';
import { useEffect, useMemo, useRef } from 'react';
import { useRecoilValue } from 'recoil';
import {
    Roi,
    ROI_OAR_TYPES,
    ROI_POINT_TYPES,
    ROI_TARGET_TYPES,
} from '~/domain/dicom/dicomstructureset';
import { selectedROIState } from '~/state/atoms/structureSet/roiAtoms';
import {
    currentStructureSetState,
    StructureSetState,
} from '~/state/atoms/structureSet/structureSetAtoms';
import StructureList from './StructureList';
import StructureSetDetails from './StructureSetDetails';

export default function StructureSet({
    structureSet,
    deleteRoi,
}: {
    structureSet: StructureSetState;
    deleteRoi: (roiNumber: number, sopUid: string) => void;
}) {
    const menuRef = useRef<HTMLElement>();
    const itemRefs = useRef<{ [key: number]: HTMLDivElement | null }>({});
    const selectedROI = useRecoilValue(selectedROIState);
    const currentStructureSet = useRecoilValue(currentStructureSetState);

    const { targets, organsAtRisk, pointStructures, otherStructures } =
        useMemo(() => {
            if (!currentStructureSet?.roiArray) {
                return {
                    targets: [],
                    organsAtRisk: [],
                    pointStructures: [],
                    otherStructures: [],
                };
            }

            const roiArray = currentStructureSet.roiArray;

            const targets = roiArray.filter((roi) =>
                ROI_TARGET_TYPES.includes(roi.interpretedType)
            );
            const oars = roiArray.filter((roi) =>
                ROI_OAR_TYPES.includes(roi.interpretedType)
            );
            const points = roiArray.filter((roi) =>
                ROI_POINT_TYPES.includes(roi.interpretedType)
            );
            const others = roiArray.filter(
                (roi) =>
                    !ROI_TARGET_TYPES.includes(roi.interpretedType) &&
                    !ROI_OAR_TYPES.includes(roi.interpretedType) &&
                    !ROI_POINT_TYPES.includes(roi.interpretedType)
            );

            const sortByName = (a: Roi, b: Roi) => a.name.localeCompare(b.name);

            return {
                targets: targets.sort(sortByName),
                organsAtRisk: oars.sort(sortByName),
                pointStructures: points.sort(sortByName),
                otherStructures: others.sort(sortByName),
            };
        }, [
            currentStructureSet?.dicomStructureSet.rois,
            currentStructureSet?.roiArray,
            structureSet,
        ]);

    useEffect(() => {
        if (selectedROI && itemRefs.current[selectedROI]) {
            itemRefs.current[selectedROI]?.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
            });
        }
    }, [selectedROI]);

    const assignItemRef = (roiNumber: number, el: HTMLDivElement | null) => {
        itemRefs.current[roiNumber] = el;
    };

    return (
        <Box
            position="relative"
            height="100%"
            display="grid"
            gridTemplateRows="auto 1fr"
            gap={1}
        >
            <StructureSetDetails
                structureSetLabel={structureSet.structureSetLabel}
                approvalStatus={structureSet.approvalStatus}
                generationModel={
                    currentStructureSet?.dicomStructureSet?.generationModel
                }
            />
            <Box position="relative" height="100% " ref={menuRef}>
                <Box position="absolute" width="100%" height="100%">
                    <List
                        sx={{
                            position: 'static',
                            overflowY: 'auto',
                            overflowX: 'hidden',
                            height: '100%',
                            '& ul': { padding: 0 },
                        }}
                    >
                        {currentStructureSet && (
                            <>
                                <StructureList
                                    title="Targets"
                                    rois={targets}
                                    currentStructureSetSopInstanceUID={
                                        currentStructureSet.dicomStructureSet
                                            .sopInstanceUID
                                    }
                                    onDelete={deleteRoi}
                                    assignRef={assignItemRef}
                                    initialExpanded={true}
                                    menuRef={menuRef}
                                />
                                <StructureList
                                    title="Organs at risk"
                                    rois={organsAtRisk}
                                    currentStructureSetSopInstanceUID={
                                        currentStructureSet.dicomStructureSet
                                            .sopInstanceUID
                                    }
                                    onDelete={deleteRoi}
                                    assignRef={assignItemRef}
                                    initialExpanded={true}
                                    menuRef={menuRef}
                                />
                                <StructureList
                                    title="Other ROIs"
                                    rois={otherStructures}
                                    currentStructureSetSopInstanceUID={
                                        currentStructureSet.dicomStructureSet
                                            .sopInstanceUID
                                    }
                                    onDelete={deleteRoi}
                                    assignRef={assignItemRef}
                                    initialExpanded={true}
                                    menuRef={menuRef}
                                />
                                <StructureList
                                    title="Points"
                                    rois={pointStructures}
                                    currentStructureSetSopInstanceUID={
                                        currentStructureSet.dicomStructureSet
                                            .sopInstanceUID
                                    }
                                    onDelete={deleteRoi}
                                    assignRef={assignItemRef}
                                    initialExpanded={true}
                                    menuRef={menuRef}
                                />
                            </>
                        )}
                    </List>
                </Box>
            </Box>
        </Box>
    );
}
